// src/components/ecommerce/PromotionsManager.jsx
import React, { useState, useEffect } from 'react';
import { 
  Tag, Plus, Edit, Trash2, Eye, EyeOff, Calendar, Percent,
  Gift, Clock, Star, Image as ImageIcon, Settings, X
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';
import { FileUpload } from '../common/FileUpload';
import toast from 'react-hot-toast';

export const PromotionsManager = ({ storeId, bookData, onUpdate }) => {
  const [promoSettings, setPromoSettings] = useState({
    enabled: true,
    title: 'Special Offers',
    layout: 'banner', // banner, carousel, grid
    autoplay: true,
    slideDuration: 7000,
    showCountdown: true
  });

  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPromo, setEditingPromo] = useState(null);
  const [showCreatePromo, setShowCreatePromo] = useState(false);

  useEffect(() => {
    loadData();
  }, [storeId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [promos, settings] = await Promise.all([
        ecommerceService.getPromotions(storeId),
        ecommerceService.getPromotionSettings(storeId)
      ]);

      setPromotions(promos || []);
      if (settings) {
        setPromoSettings(settings);
      }
    } catch (error) {
      console.error('Error loading promotions data:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePromoSettings = async (settings = promoSettings) => {
    try {
      await ecommerceService.updatePromotionSettings(storeId, settings);
      setPromoSettings(settings);
      onUpdate?.({ promotions: settings });
      toast.success('Promotion settings updated successfully');
    } catch (error) {
      console.error('Error saving promotion settings:', error);
      toast.error('Failed to update promotion settings');
    }
  };

  const createPromotion = async (promoData) => {
    try {
      const newPromo = await ecommerceService.createPromotion(storeId, {
        ...promoData,
        id: `promo-${Date.now()}`,
        createdAt: new Date().toISOString(),
        active: true
      });
      
      setPromotions(prev => [...prev, newPromo]);
      toast.success('Promotion created successfully');
      return newPromo;
    } catch (error) {
      console.error('Error creating promotion:', error);
      toast.error('Failed to create promotion');
      throw error;
    }
  };

  const updatePromotion = async (promoId, updates) => {
    try {
      const updatedPromo = await ecommerceService.updatePromotion(storeId, promoId, updates);
      setPromotions(prev => prev.map(promo => 
        promo.id === promoId ? { ...promo, ...updates } : promo
      ));
      toast.success('Promotion updated successfully');
      return updatedPromo;
    } catch (error) {
      console.error('Error updating promotion:', error);
      toast.error('Failed to update promotion');
      throw error;
    }
  };

  const deletePromotion = async (promoId) => {
    try {
      await ecommerceService.deletePromotion(storeId, promoId);
      setPromotions(prev => prev.filter(promo => promo.id !== promoId));
      toast.success('Promotion deleted successfully');
    } catch (error) {
      console.error('Error deleting promotion:', error);
      toast.error('Failed to delete promotion');
    }
  };

  const togglePromotion = async (promoId, active) => {
    try {
      await updatePromotion(promoId, { active });
    } catch (error) {
      // Error already handled in updatePromotion
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Tag className="h-6 w-6 text-red-500" />
            Promotions Manager
          </h2>
          <p className="text-gray-600">Manage your store's special offers and promotional banners</p>
        </div>
        <button
          onClick={() => setShowCreatePromo(true)}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Promotion
        </button>
      </div>

      {/* Settings */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Promotion Section Settings
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
            <input
              type="text"
              value={promoSettings.title}
              onChange={(e) => {
                const updated = { ...promoSettings, title: e.target.value };
                setPromoSettings(updated);
                savePromoSettings(updated);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Layout</label>
            <select
              value={promoSettings.layout}
              onChange={(e) => {
                const updated = { ...promoSettings, layout: e.target.value };
                setPromoSettings(updated);
                savePromoSettings(updated);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="banner">Banner</option>
              <option value="carousel">Carousel</option>
              <option value="grid">Grid</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Slide Duration (ms)</label>
            <input
              type="number"
              value={promoSettings.slideDuration}
              onChange={(e) => {
                const updated = { ...promoSettings, slideDuration: parseInt(e.target.value) };
                setPromoSettings(updated);
                savePromoSettings(updated);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              min="1000"
              step="500"
            />
          </div>

          <div className="flex items-center space-x-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={promoSettings.autoplay}
                onChange={(e) => {
                  const updated = { ...promoSettings, autoplay: e.target.checked };
                  setPromoSettings(updated);
                  savePromoSettings(updated);
                }}
                className="sr-only"
              />
              <div className={`relative inline-block w-8 h-5 transition duration-200 ease-linear rounded-full ${
                promoSettings.autoplay ? 'bg-red-500' : 'bg-gray-300'
              }`}>
                <div className={`absolute left-0 top-0 bg-white border-2 border-gray-300 rounded-full h-5 w-5 transition transform duration-100 ease-linear ${
                  promoSettings.autoplay ? 'translate-x-full border-red-500' : 'translate-x-0'
                }`}></div>
              </div>
              <span className="ml-2 text-sm font-medium text-gray-700">Autoplay</span>
            </label>

            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={promoSettings.showCountdown}
                onChange={(e) => {
                  const updated = { ...promoSettings, showCountdown: e.target.checked };
                  setPromoSettings(updated);
                  savePromoSettings(updated);
                }}
                className="sr-only"
              />
              <div className={`relative inline-block w-8 h-5 transition duration-200 ease-linear rounded-full ${
                promoSettings.showCountdown ? 'bg-red-500' : 'bg-gray-300'
              }`}>
                <div className={`absolute left-0 top-0 bg-white border-2 border-gray-300 rounded-full h-5 w-5 transition transform duration-100 ease-linear ${
                  promoSettings.showCountdown ? 'translate-x-full border-red-500' : 'translate-x-0'
                }`}></div>
              </div>
              <span className="ml-2 text-sm font-medium text-gray-700">Show Countdown</span>
            </label>
          </div>
        </div>
      </div>

      {/* Promotions List */}
      <div className="bg-white border rounded-lg">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Gift className="h-5 w-5 text-red-500" />
            Active Promotions ({promotions.filter(p => p.active).length})
          </h3>
        </div>
        
        <div className="p-6">
          {promotions.length === 0 ? (
            <div className="text-center py-12">
              <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Promotions</h3>
              <p className="text-gray-600 mb-4">Create promotional banners to highlight special offers and drive sales.</p>
              <button
                onClick={() => setShowCreatePromo(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Create Your First Promotion
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {promotions.map((promo) => (
                <div key={promo.id} className={`border rounded-lg p-4 ${promo.active ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Promotion Image */}
                      <div className="w-20 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                        {promo.image ? (
                          <img
                            src={promo.image}
                            alt={promo.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Promotion Details */}
                      <div>
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          {promo.title}
                          {promo.type === 'percentage' && <Percent className="h-4 w-4 text-red-500" />}
                          {promo.type === 'fixed' && <Tag className="h-4 w-4 text-red-500" />}
                        </h4>
                        <p className="text-sm text-gray-600">{promo.description}</p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                          <span>Valid: {new Date(promo.startDate).toLocaleDateString()} - {new Date(promo.endDate).toLocaleDateString()}</span>
                          {promo.discountValue && (
                            <span className="bg-red-100 text-red-800 px-2 py-1 rounded">
                              {promo.type === 'percentage' ? `${promo.discountValue}% OFF` : `R${promo.discountValue} OFF`}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => togglePromotion(promo.id, !promo.active)}
                        className={`p-2 rounded-lg ${promo.active ? 'text-green-600 hover:bg-green-100' : 'text-gray-400 hover:bg-gray-100'}`}
                      >
                        {promo.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                      
                      <button
                        onClick={() => setEditingPromo(promo)}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-100 rounded-lg"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      
                      <button
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete this promotion?')) {
                            deletePromotion(promo.id);
                          }
                        }}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-100 rounded-lg"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Promotion Modal */}
      {(showCreatePromo || editingPromo) && (
        <PromotionEditor
          promotion={editingPromo}
          onSave={editingPromo ? 
            (updates) => updatePromotion(editingPromo.id, updates) : 
            createPromotion
          }
          onClose={() => {
            setShowCreatePromo(false);
            setEditingPromo(null);
          }}
          storeId={storeId}
        />
      )}
    </div>
  );
};

// Promotion Editor Component
const PromotionEditor = ({ promotion, onSave, onClose, storeId }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'percentage', // percentage, fixed, freeShipping
    discountValue: 0,
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    image: '',
    ctaText: 'Shop Now',
    backgroundColor: '#ef4444',
    textColor: '#ffffff',
    ...promotion
  });

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      // Error already handled in parent
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    try {
        const { firestoreService } = await import('../../services/firestoreService');
        const uploadResult = await firestoreService.uploadFile(file, `pb-${storeId}/sections/promotions/images`, {
        maxSize: 10 * 1024 * 1024, // 10MB
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
        });
        setFormData(prev => ({ ...prev, image: uploadResult.url }));
        toast.success('Image uploaded successfully');
    } catch (error) {
        console.error('Error uploading image:', error);
        toast.error('Failed to upload image');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {promotion ? 'Edit Promotion' : 'Create New Promotion'}
            </h3>
            <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Promotion Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Text</label>
                <input
                  type="text"
                  value={formData.ctaText}
                  onChange={(e) => setFormData(prev => ({ ...prev, ctaText: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                rows="3"
              />
            </div>
            
            {/* Discount Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Discount Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="percentage">Percentage Off</option>
                  <option value="fixed">Fixed Amount Off</option>
                  <option value="freeShipping">Free Shipping</option>
                </select>
              </div>
              
              {formData.type !== 'freeShipping' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount Value {formData.type === 'percentage' ? '(%)' : '(R)'}
                  </label>
                  <input
                    type="number"
                    value={formData.discountValue}
                    onChange={(e) => setFormData(prev => ({ ...prev, discountValue: parseFloat(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    min="0"
                    step="0.01"
                  />
                </div>
              )}
            </div>
            
            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  min={formData.startDate}
                  required
                />
              </div>
            </div>
            
            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Promotion Image</label>
              <FileUpload
                onFileSelect={async (file, error) => {
                    if (error) {
                    toast.error(`Upload error: ${error}`);
                    return;
                    }
                    await handleImageUpload(file);
                }}
                currentFile={formData.image}
                onRemove={() => setFormData(prev => ({ ...prev, image: '' }))}
                accept="image/*"
                maxSize={10}
                label="Upload Promotion Image"
                showPreview={true}
              />
            </div>
            
            {/* Colors */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
                <input
                  type="color"
                  value={formData.backgroundColor}
                  onChange={(e) => setFormData(prev => ({ ...prev, backgroundColor: e.target.value }))}
                  className="w-full h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
                <input
                  type="color"
                  value={formData.textColor}
                  onChange={(e) => setFormData(prev => ({ ...prev, textColor: e.target.value }))}
                  className="w-full h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
          </div>
          
          <div className="p-6 border-t flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : (promotion ? 'Update Promotion' : 'Create Promotion')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};